import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, CircularProgress } from '@mui/material';
import { API_URLS } from '../../config';

interface AdminRouteProps {
  children: React.ReactNode;
}

/**
 * Protected route component that only allows admin users
 * Checks Auth0 user_metadata.admin field via backend
 */
export const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    checkAdminStatus();
  }, [isAuthenticated]);

  const checkAdminStatus = async () => {
    if (!isAuthenticated) {
      setChecking(false);
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      const resp = await fetch(`${API_URLS.BASE}/api/admin/moderation/check-access`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (resp.status === 403) {
        setIsAdmin(false);
      } else if (resp.ok) {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
      }
    } catch (err) {
      console.error('Error checking admin status:', err);
      setIsAdmin(false);
    } finally {
      setChecking(false);
    }
  };

  // Show loading spinner while checking auth and admin status
  if (isLoading || checking) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh'
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to dashboard if not admin
  if (isAdmin === false) {
    return <Navigate to="/dashboard" replace />;
  }

  // Render children if admin
  return <>{children}</>;
};
